import { ChevronRight, AlertTriangle, ExternalLink } from "lucide-react";

// Reusable building blocks for long-form content pages. Mirrors the in-line
// components already used by /fr/demarches and /fr/entrepreneuriat so any new
// content page can stay visually consistent without copy-pasting markup.

// Inline italic styling for Polish terms.
export function PL({ children }) {
  return <em className="italic text-slate-700">{children}</em>;
}

// H3 sub-heading.
export function Sub({ children }) {
  return <h3 className="font-display text-lg font-semibold text-slate-900 mt-6 mb-2">{children}</h3>;
}

// Amber callout ("À noter", warnings, side-bar disclaimers).
export function Warn({ children, testid }) {
  return (
    <aside
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 flex items-start gap-3"
      data-testid={testid}
    >
      <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-amber-900 leading-relaxed">{children}</p>
    </aside>
  );
}

// One numbered H2 section. `n` is the displayed number, `id` is the anchor.
export function ContentSection({ id, n, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-14" data-testid={`section-${id}`}>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">
        <span className="text-slate-400 mr-2 tabular-nums">{n}.</span>
        {title}
      </h2>
      <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

// Auto-generated TOC. Takes the same SECTIONS array used to render the body,
// so the two cannot drift out of sync.
export function AutoTOC({ sections, testid = "toc" }) {
  return (
    <section className="mb-16" data-testid={testid}>
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Sommaire</h2>
      <ol className="space-y-2 list-none">
        {sections.map((s, i) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="group flex items-baseline gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition"
              data-testid={`toc-${s.id}`}
            >
              <span className="font-display text-sm font-semibold text-slate-400 group-hover:text-[#0056D2] tabular-nums w-6">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-slate-700 group-hover:text-[#0056D2] flex-1">{s.title}</span>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#0056D2] self-center" />
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}

// "À retenir" block. Accepts arbitrary children so callers can choose between
// a bullet list and a free-form paragraph (the blog uses the latter).
export function KeyPointsBlock({ children, testid = "key-points" }) {
  return (
    <section
      className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 mb-12"
      data-testid={testid}
    >
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">À retenir</h2>
      <div className="text-slate-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

// Sources list. Each entry: { url, label, description? }.
export function Sources({ sources, testid = "sources" }) {
  return (
    <section id="sources" className="scroll-mt-24 mb-12" data-testid={testid}>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-5">Sources</h2>
      {sources.length === 0 ? (
        <p className="text-slate-400 italic" data-testid="sources-placeholder">
          Bibliographie à insérer.
        </p>
      ) : (
        <ol className="space-y-5 list-none">
          {sources.map((s, i) => (
            <li
              key={s.url}
              id={`source-${i + 1}`}
              className="flex items-baseline gap-3 scroll-mt-24"
              data-testid={`source-${i + 1}`}
            >
              <span className="font-display text-sm font-semibold text-slate-400 tabular-nums w-6 flex-shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-1.5 text-[#0056D2] hover:underline break-words"
                  data-testid={`source-link-${i + 1}`}
                >
                  <span>{s.label}</span>
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-1" />
                </a>
                {s.description && (
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{s.description}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
